/**
 * ThinkingBubble — MS203 in-thread loading affordance.
 *
 * Replaces the previous `<ChatTypingIndicator>` (three-dot pulse
 * inside a chat bubble) for the moment when the assistant is
 * generating its next turn. Wraps the existing ThinkingStrip
 * (Sparkles / morph + rotating verb + 3-dot wave) in the same
 * rounded-2xl chat-bubble chrome the dot indicator used, so the
 * "AI is thinking" affordance now lives in the same visual slot
 * where the next assistant message will appear.
 *
 * Lucky's MS203 direction (paraphrasing 2026-05-17 design review):
 *
 *   "The Thinking Animations should probably replace the current
 *    three-dot loading animation inside the chat widget. I think
 *    that that's the best place to put it."
 *
 * The three-dot wave is preserved (it's part of ThinkingStrip);
 * the difference is the surrounding affordance — instead of a
 * naked tiny pill, you now see the same alive-mascot icon + verb
 * cadence that previously sat below the composer, but inside the
 * chat thread itself. The visual transition from "thinking" to
 * "assistant message" is seamless: same bubble shape, content
 * swaps.
 *
 * This is a tactical partial implementation of ADR-0007
 * (single-glyph in-bubble affordance). ADR-0007 proposed dropping
 * the icon cycle entirely + event-driven verbs. Lucky's call here
 * is "keep the existing icon + verb pattern, just move it into
 * the right slot." Less ambitious; lands the visual fix sooner.
 * ADR-0007 implementation (MS197-200 sequence in that doc) can
 * still happen later if dynamic verbs become a priority.
 */

import { ThinkingStrip, LEMBRA_VERB_VOCAB } from "@/components/lembra/ThinkingStrip";

interface ThinkingBubbleProps {
  /** Optional first name of the recipient if the chat session has
   *  one loaded. When provided, the bubble swaps to the
   *  personalised verb cycle so the alive-mascot reflects that the
   *  model is recalling them, not just generating generically. */
  recipientFirstName?: string | null;
  /** Override the default verb vocab. Most callers should leave
   *  this unset and rely on the recipient-aware default. Useful
   *  for callers with a non-recommendation pipeline (e.g. the
   *  remoteAcknowledge path could pass `LEMBRA_VERB_VOCAB.remoteAcknowledge`). */
  verbs?: string[];
}

export function ThinkingBubble({
  recipientFirstName = null,
  verbs: verbsOverride,
}: ThinkingBubbleProps) {
  // Default verb resolution — mirrors the logic ComposerInput used
  // to do at its own ThinkingStrip mount, lifted here so callers
  // don't have to re-implement.
  const verbs = (() => {
    if (verbsOverride) return verbsOverride;
    const trimmedName = recipientFirstName?.trim();
    if (!trimmedName) return [...LEMBRA_VERB_VOCAB.conciergeRecommend];
    return LEMBRA_VERB_VOCAB.conciergeRecommendPersonalised.map((v) =>
      v.replace(/\{name\}/g, trimmedName),
    );
  })();

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Lembra is thinking"
      // Same chrome as the old ChatTypingIndicator — rounded-2xl
      // navy/cream bubble — so the visual continuity with a real
      // assistant message bubble is preserved. The contents swap
      // (thinking strip → markdown text + cards) but the bubble
      // shell stays put.
      className="inline-flex items-center rounded-2xl bg-charcoal/5 px-4 py-3"
    >
      {/* MS280: defaults to morph glyph + 2000ms interval (synced
          with the glyph's 2s-per-frame cycle), no 3-dot pulse. The
          old explicit intervalMs={2500} + glyph="morph" props are
          now the defaults so they're not needed. Density "default"
          is also the default. */}
      <ThinkingStrip active verbs={verbs} />
    </div>
  );
}
